<template>
  <div
    ref="root"
    class="searchableCombo"
    :class="{ searchableComboEditing: editing }"
  >
    <button
      v-if="!editing"
      type="button"
      class="searchableComboValue"
      :class="{ searchableComboValueSelected: selectedItem }"
      :title="selectedItem ? selectedItem.nome : placeholder"
      @click="startEditing"
    >
      <i class="fa fa-calendar-o" aria-hidden="true"></i>
      <span>
        <small>Evento</small>
        <strong>{{ selectedItem ? selectedItem.nome : placeholder }}</strong>
      </span>
      <i
        class="fa"
        :class="selectedItem ? 'fa-pencil' : 'fa-angle-down'"
        aria-hidden="true"
      ></i>
    </button>

    <button
      v-if="!editing && selectedItem"
      type="button"
      class="searchableComboClearSelected"
      title="Limpar evento"
      aria-label="Limpar evento selecionado"
      @click.stop="clearSelection"
    >
      <i class="fa fa-times" aria-hidden="true"></i>
    </button>

    <div v-if="editing" class="searchableComboEditor">
      <i class="fa fa-search" aria-hidden="true"></i>
      <input
        ref="input"
        v-model="query"
        type="search"
        autocomplete="off"
        :placeholder="selectedItem ? 'Buscar outro evento' : 'Buscar evento'"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="editing ? 'true' : 'false'"
        @keydown.down.prevent="moveActive(1)"
        @keydown.up.prevent="moveActive(-1)"
        @keydown.enter.prevent="selectActive"
        @keydown.esc.prevent="stopEditing"
      />
      <button
        type="button"
        title="Fechar busca"
        aria-label="Fechar busca de eventos"
        @click="stopEditing"
      >
        <i class="fa fa-times" aria-hidden="true"></i>
      </button>
    </div>

    <div v-if="editing" class="searchableComboDropdown" role="listbox">
      <button
        type="button"
        class="searchableComboOption searchableComboOptionAll"
        :class="{ active: activeIndex === -1 }"
        role="option"
        :aria-selected="!value ? 'true' : 'false'"
        @mousedown.prevent="selectItem(null)"
      >
        <i class="fa fa-list" aria-hidden="true"></i>
        <span>
          <strong>Todos os eventos</strong>
          <small>Remover o filtro de evento</small>
        </span>
      </button>

      <button
        v-for="(item, index) in filteredItems"
        :key="item.id"
        type="button"
        class="searchableComboOption"
        :class="{ active: activeIndex === index }"
        role="option"
        :aria-selected="item.id === value ? 'true' : 'false'"
        @mouseenter="activeIndex = index"
        @mousedown.prevent="selectItem(item)"
      >
        <i
          class="fa"
          :class="item.id === value ? 'fa-check-circle' : 'fa-calendar-o'"
          aria-hidden="true"
        ></i>
        <span>
          <strong>{{ item.nome }}</strong>
          <small v-if="item.id === value">Selecionado</small>
        </span>
      </button>

      <div v-if="!filteredItems.length" class="searchableComboEmpty">
        <i class="fa fa-search" aria-hidden="true"></i>
        <span>Nenhum evento encontrado</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SearchableCombo",
  props: {
    itens: { type: Array, default: () => [] },
    placeholder: { type: String, default: "Todos os eventos" },
    value: { type: String, default: "" },
  },
  computed: {
    filteredItems() {
      var query = this.normalize(this.query);
      var itens = this.itens || [];

      if (!query) {
        return itens.slice(0, 80);
      }

      return itens
        .filter((item) => this.normalize(item.nome).includes(query))
        .slice(0, 80);
    },
    selectedItem() {
      return (this.itens || []).find((item) => item.id === this.value) || null;
    },
  },
  watch: {
    query() {
      this.activeIndex = this.filteredItems.length ? 0 : -1;
    },
  },
  methods: {
    clearSelection() {
      this.$emit("input", "");
      this.$emit("change", "");
    },
    handleOutsideClick(event) {
      if (
        this.editing &&
        this.$refs.root &&
        !this.$refs.root.contains(event.target)
      ) {
        this.stopEditing();
      }
    },
    moveActive(direction) {
      if (!this.filteredItems.length) {
        this.activeIndex = -1;
        return;
      }

      var nextIndex = this.activeIndex + direction;

      if (nextIndex < -1) {
        nextIndex = this.filteredItems.length - 1;
      } else if (nextIndex >= this.filteredItems.length) {
        nextIndex = -1;
      }

      this.activeIndex = nextIndex;
    },
    normalize(value) {
      return (value || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    selectActive() {
      if (this.activeIndex === -1) {
        this.selectItem(null);
        return;
      }

      this.selectItem(this.filteredItems[this.activeIndex]);
    },
    selectItem(item) {
      var value = item ? item.id : "";
      this.$emit("input", value);
      this.$emit("change", value);
      this.stopEditing();
    },
    startEditing() {
      this.editing = true;
      this.query = "";
      this.activeIndex = this.filteredItems.length ? 0 : -1;
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    stopEditing() {
      this.editing = false;
      this.query = "";
      this.activeIndex = -1;
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  },
  data() {
    return {
      activeIndex: -1,
      editing: false,
      query: "",
    };
  },
};
</script>

<style>
.searchableCombo {
  height: 34px;
  min-width: 0;
  position: relative;
}

.searchableComboValue {
  align-items: center;
  background: #ffffff;
  border: 1px solid #cfd5d9;
  color: #30383e;
  display: grid;
  gap: 8px;
  grid-template-columns: 16px minmax(0, 1fr) 12px;
  height: 34px;
  padding: 0 9px;
  text-align: left;
  width: 100%;
}

.searchableComboValue > i:first-child {
  color: #58776a;
}

.searchableComboValue > i:last-child {
  color: #79838a;
  font-size: 9px;
}

.searchableComboValue span {
  min-width: 0;
}

.searchableComboValue small,
.searchableComboValue strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.searchableComboValue small {
  color: #7b858c;
  font-size: 7px;
  line-height: 1;
  text-transform: uppercase;
}

.searchableComboValue strong {
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  margin-top: 2px;
}

.searchableComboClearSelected {
  align-items: center;
  background: #ffffff;
  border: 0;
  border-left: 1px solid #dce1e4;
  color: #7a858c;
  display: flex;
  height: 32px;
  justify-content: center;
  position: absolute;
  right: 1px;
  top: 1px;
  width: 28px;
}

.searchableComboValueSelected {
  padding-right: 38px;
}

.searchableComboEditor {
  align-items: center;
  background: #ffffff;
  border: 1px solid #3f8064;
  display: flex;
  height: 34px;
  position: relative;
  z-index: 31;
}

.searchableComboEditor > i {
  color: #4b8069;
  margin: 0 8px;
}

.searchableComboEditor input {
  background: transparent;
  border: 0;
  color: #30383e;
  font-size: 11px;
  height: 100%;
  min-width: 0;
  outline: 0;
  padding: 0;
  width: 100%;
}

.searchableComboEditor button {
  background: transparent;
  border: 0;
  color: #748087;
  height: 100%;
  width: 30px;
}

.searchableComboDropdown {
  background: #ffffff;
  border: 1px solid #aeb8be;
  box-shadow: 0 8px 20px rgba(35, 43, 48, 0.18);
  left: 0;
  max-height: 310px;
  overflow-y: auto;
  position: absolute;
  right: 0;
  top: 37px;
  z-index: 30;
}

.searchableComboOption {
  align-items: center;
  background: #ffffff;
  border: 0;
  border-bottom: 1px solid #edf0f2;
  color: #30383e;
  display: grid;
  gap: 9px;
  grid-template-columns: 15px minmax(0, 1fr);
  min-height: 42px;
  padding: 7px 10px;
  text-align: left;
  width: 100%;
}

.searchableComboOption:last-of-type {
  border-bottom: 0;
}

.searchableComboOption:hover,
.searchableComboOption.active {
  background: #edf5f1;
}

.searchableComboOption > i {
  color: #5c7c6e;
}

.searchableComboOption span {
  min-width: 0;
}

.searchableComboOption strong,
.searchableComboOption small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.searchableComboOption strong {
  font-size: 10px;
  font-weight: 600;
}

.searchableComboOption small {
  color: #778189;
  font-size: 8px;
  margin-top: 2px;
}

.searchableComboOptionAll {
  background: #f5f7f8;
}

.searchableComboEmpty {
  align-items: center;
  color: #7b858c;
  display: flex;
  flex-direction: column;
  font-size: 9px;
  gap: 7px;
  justify-content: center;
  min-height: 82px;
}

.body-dark .searchableComboValue,
.body-dark .searchableComboClearSelected,
.body-dark .searchableComboEditor,
.body-dark .searchableComboDropdown,
.body-dark .searchableComboOption {
  background: var(--dark-surface) !important;
  border-color: var(--dark-border) !important;
  color: var(--dark-text-soft) !important;
}

.body-dark .searchableComboOptionAll,
.body-dark .searchableComboOption:hover,
.body-dark .searchableComboOption.active {
  background: var(--dark-surface-alt) !important;
}

.body-dark .searchableComboValue small,
.body-dark .searchableComboOption small,
.body-dark .searchableComboEditor input,
.body-dark .searchableComboEmpty {
  color: var(--dark-muted) !important;
}
</style>
